import React, { useState, useEffect, useMemo } from 'react';
import { Activity, TrendingUp, TrendingDown, Truck, Calendar, CheckCircle, Clock } from 'lucide-react';
import { Project } from '../types';
import { getAllProjects } from '../services/projectService';
import { Timestamp } from 'firebase/firestore';

function toDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  return null;
}

function isInCurrentMonth(ts: Timestamp | null | undefined): boolean {
  const d = toDate(ts);
  if (!d) return false;
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function isInLastMonth(ts: Timestamp | null | undefined): boolean {
  const d = toDate(ts);
  if (!d) return false;
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return d.getMonth() === lastMonth.getMonth() && d.getFullYear() === lastMonth.getFullYear();
}

function isInNextDays(ts: Timestamp | null | undefined, days: number): boolean {
  const d = toDate(ts);
  if (!d) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const future = new Date(now);
  future.setDate(future.getDate() + days);
  return d >= now && d <= future;
}

interface MetricCardProps {
  icon: React.FC<any>;
  label: string;
  value: number;
  comparison?: { value: number; label: string };
  color: string;
  iconBg: string;
}

function MetricCard({ icon: Icon, label, value, comparison, color, iconBg }: MetricCardProps) {
  const trend = comparison ? value - comparison.value : 0;

  return (
    <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)] p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon size={22} className={color} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-2xl font-semibold text-[var(--text-primary)] leading-none">{value}</p>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{label}</p>
        {comparison && (
          <div className="flex items-center gap-1 mt-1.5">
            {trend > 0 ? (
              <TrendingUp size={14} className="text-emerald-500" />
            ) : trend < 0 ? (
              <TrendingDown size={14} className="text-amber-500" />
            ) : null}
            <span className={`text-xs font-medium ${
              trend > 0 ? 'text-emerald-600 dark:text-emerald-400' : trend < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--text-tertiary)]'
            }`}>
              {trend > 0 ? '+' : ''}{trend} {comparison.label}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

interface DashboardMetricsProps {
  className?: string;
  /** Render the cards always open without the toggle, e.g. inside the Mer sheet. */
  embedded?: boolean;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ className = '', embedded = false }) => {
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    // Collapsed by default. These six metrics are read-only — there is nothing
    // to tap — so on a phone they pushed the first project (the reason the
    // screen exists) below the fold on every visit. An explicit choice either
    // way is still honoured; only the unset case changed.
    const stored = localStorage.getItem('stadsauktion_metrics_collapsed');
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const projects = await getAllProjects();
        setAllProjects(projects);
      } catch (e) {
        console.error('Error loading metrics data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const metrics = useMemo(() => {
    if (!allProjects.length) return null;

    const active = allProjects.filter(p => !['avslutad', 'avbruten'].includes(p.status));
    const newThisMonth = allProjects.filter(p => isInCurrentMonth(p.createdAt));
    const newLastMonth = allProjects.filter(p => isInLastMonth(p.createdAt));
    const upcomingPickups = allProjects.filter(p => isInNextDays(p.dates.scheduledPickup, 7));
    const upcomingEvaluations = allProjects.filter(p => isInNextDays(p.dates.evaluation, 7));
    const completedThisMonth = allProjects.filter(p => p.status === 'avslutad' && isInCurrentMonth(p.updatedAt));
    const waiting = allProjects.filter(p => p.status === 'väntar');

    return {
      active: active.length,
      newThisMonth: newThisMonth.length,
      newLastMonth: newLastMonth.length,
      upcomingPickups: upcomingPickups.length,
      upcomingEvaluations: upcomingEvaluations.length,
      completedThisMonth: completedThisMonth.length,
      waiting: waiting.length,
    };
  }, [allProjects]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('stadsauktion_metrics_collapsed', String(next));
  };

  if (loading) {
    return (
      <div className={`mb-4 ${className}`}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)] p-4 h-20 animate-pulse">
              <div className="w-9 h-3 bg-[var(--bg-surface-alt)] rounded mb-2" />
              <div className="w-16 h-3 bg-[var(--bg-surface-alt)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className={`mb-4 ${className}`}>
      {!embedded && (
        <button
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          className="flex items-center gap-1.5 -ml-1 px-1 min-h-[44px] text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <Activity size={14} />
          <span>{collapsed ? 'Visa statistik' : 'Dölj statistik'}</span>
        </button>
      )}

      {(embedded || !collapsed) && (
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 animate-fade-in ${embedded ? '' : '[&>:nth-child(n+5)]:hidden [&>:nth-child(n+5)]:md:block'}`}>
          <MetricCard
            icon={Activity}
            label="Aktiva projekt"
            value={metrics.active}
            color="text-accent-600 dark:text-accent-400"
            iconBg="bg-accent-50 dark:bg-accent-900/30"
          />
          <MetricCard
            icon={Calendar}
            label="Nya denna månad"
            value={metrics.newThisMonth}
            comparison={{ value: metrics.newLastMonth, label: 'vs förra mån' }}
            color="text-blue-600 dark:text-blue-400"
            iconBg="bg-blue-50 dark:bg-blue-900/30"
          />
          <MetricCard
            icon={Truck}
            label="Hämtningar 7 dagar"
            value={metrics.upcomingPickups}
            color="text-emerald-600 dark:text-emerald-400"
            iconBg="bg-emerald-50 dark:bg-emerald-900/30"
          />
          <MetricCard
            icon={Clock}
            label="Hembesök 7 dagar"
            value={metrics.upcomingEvaluations}
            color="text-violet-600 dark:text-violet-400"
            iconBg="bg-violet-50 dark:bg-violet-900/30"
          />
          <MetricCard
            icon={CheckCircle}
            label="Avslutade denna månad"
            value={metrics.completedThisMonth}
            color="text-emerald-600 dark:text-emerald-400"
            iconBg="bg-emerald-50 dark:bg-emerald-900/30"
          />
          <MetricCard
            icon={Clock}
            label="Väntar på åtgärd"
            value={metrics.waiting}
            color="text-amber-600 dark:text-amber-400"
            iconBg="bg-amber-50 dark:bg-amber-900/30"
          />
        </div>
      )}
    </div>
  );
};

export default DashboardMetrics;
